import React, { useState, useEffect, useRef } from "react";
import {
  X,
  RefreshCw,
  Loader2,
  ClipboardCheck,
  Disc3,
  Compass,
  ChevronDown,
  AlertCircle,
  PlugZap,
  Unplug,
  ChevronLeft,
  BadgeCheck,
  CheckCircle2,
} from "lucide-react";
import StatusControl from "./Manufacturing/StatusControl";
import ValidationFlow from "./Manufacturing/ValidationFlow";
import AttemptHistory from "./Manufacturing/AttemptHistory";
import SessionHistory from "./Manufacturing/SessionHistory";
import SessionWearablesModal from "./Manufacturing/SessionWearablesModal";
import FieldLabelControl from "./Manufacturing/FieldLabelControl";
import WearableStatusBadge from "./Manufacturing/WearableStatusBadge";
import {
  formatMac,
  getStageMeta,
  formatDateTime,
  wearableIdentity,
} from "./Manufacturing/constants";
import DeviceToolsPopover from "./DeviceToolsPopover";
import LiveDeviceView from "./LiveDeviceView";
import Freshness from "./common/Freshness";
import LiveActivityFeed from "./LiveActivityFeed";
import { fwAtLeast, LIVE_STATE_FW_MIN } from "utils/wearableStatus";
import betaAPI from "v2/api";
import { useTranslation } from "react-i18next";
import { t as translate, dateLocale } from "i18n";

// Shared keyboard-focus ring — operators tab through the bench, so every
// interactive control gets a visible focus state (not just hover).
const FOCUS =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A3A]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

const fmtCalDate = (epochS) => {
  if (!epochS) return translate("devices.workbench.dateUnknown");
  const d = new Date(epochS * 1000);
  if (Number.isNaN(d.getTime()))
    return translate("devices.workbench.dateUnknown");
  return d.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Live connection state → a chip the operator can read at a glance without
// parsing a raw firmware state string. Fed by the pushed state/substate
// events (v0.5.81+), so it tracks button- and timeout-driven transitions too.
const connState = ({ waking, commandsLocked, state, sub }, t) => {
  if (waking)
    return {
      label: t("devices.connState.waking"),
      color: "#B45309",
      bg: "#FEF3E7",
      live: true,
    };
  // Amber, not red, and "Retrying" rather than "No console": the link machine
  // is still working on it. Red plus a terminal noun told the operator the
  // situation was final when it was not, which is how a recoverable state
  // became a dead end in practice as well as in code.
  if (commandsLocked)
    return {
      label: t("devices.connState.retrying"),
      color: "#B45309",
      bg: "#FEF3E7",
      live: true,
      title: t("devices.connState.retryingTitle"),
    };
  const s = String(state || "").toUpperCase();
  const p = String(sub || "").toLowerCase();
  if (s === "SESSION") {
    if (p === "arming")
      return {
        label: t("devices.connState.arming"),
        color: "#B45309",
        bg: "#FEF3E7",
        live: true,
      };
    if (p === "sched_wait")
      return {
        label: t("devices.connState.scheduled"),
        color: "#B45309",
        bg: "#FEF3E7",
      };
    return {
      label: t("devices.connState.recording"),
      color: "#15803D",
      bg: "#F0FDF4",
      live: true,
    };
  }
  if (s === "CENTRIFUGE")
    return {
      label:
        p && p !== "none"
          ? t("devices.connState.centrifugeSub", {
              sub: p.replace(/_/g, " "),
            })
          : t("devices.connState.centrifuge"),
      color: "#B45309",
      bg: "#FEF3E7",
      live: p === "recording" || p === "countdown",
    };
  if (s === "FACTORY_TEST")
    return {
      label: t("devices.connState.selfTest"),
      color: "#B45309",
      bg: "#FEF3E7",
      live: true,
    };
  if (s.startsWith("DFU"))
    return {
      label: t("devices.connState.dfu"),
      color: "#B45309",
      bg: "#FEF3E7",
    };
  if (s === "IDLE")
    return {
      label: t("devices.connState.idle"),
      color: "#15803D",
      bg: "#F0FDF4",
    };
  if (s === "STANDBY")
    return {
      label: t("devices.connState.standby"),
      color: "#15803D",
      bg: "#F0FDF4",
    };
  return {
    label: state ? String(state) : t("devices.connState.connected"),
    color: "#15803D",
    bg: "#F0FDF4",
  };
};

const colonMac = (mac) =>
  String(mac || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase()
    .replace(/(..)(?=.)/g, "$1:");

const InfoRow = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <dt className="text-[13px] text-[#6B7280]">{label}</dt>
    <dd className="text-right font-mono text-[13px] tabular-nums text-[#111827]">
      {children}
    </dd>
  </div>
);

// Segmented validation rail — one segment per stage. Reads like a unit moving
// down the line: filled = passed, bright = current, faint = ahead. Far quicker
// to parse mid-task than a percentage fill.
const StageRail = ({ stages, allComplete, completeStages, totalStages }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-1"
      role="progressbar"
      aria-valuenow={allComplete ? totalStages : completeStages}
      aria-valuemin={0}
      aria-valuemax={totalStages}
      aria-label={t("devices.workbench.validationProgress")}
    >
      {stages.map((s, i) => {
        const done = allComplete || s.status === "complete";
        const active = !allComplete && s.status === "active";
        return (
          <div
            key={s.sub_stage || s.label || i}
            className="h-1.5 flex-1 rounded-full transition-colors duration-300"
            style={{
              backgroundColor: done
                ? allComplete
                  ? "#15803D"
                  : "#E07A3A"
                : active
                ? "#F3B183"
                : "#E7E5E1",
            }}
          />
        );
      })}
    </div>
  );
};

const TabCount = ({ children, tone = "neutral" }) => (
  <span
    className={`rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
      tone === "ok"
        ? "bg-[#F0FDF4] text-[#16A34A]"
        : tone === "live"
        ? "bg-[#DCFCE7] text-[#16A34A]"
        : "bg-[#F3F4F6] text-[#6B7280]"
    }`}
  >
    {children}
  </span>
);

/**
 * The connected Wearable Ops workbench. One home per fact:
 *
 *   top bar   who the unit is, how it is connected, Device tools
 *   hero      what it is doing now, its next action, and its four readings
 *             (battery, storage, threshold, firmware) with the action that
 *             changes each one beside it
 *   tabs      Activity (live feed) · Sessions · Manufacturing
 *
 * Owns no serial logic — the modal passes the resolved wearable record, live
 * devCfg, action callbacks, the active-flow node (`centerFlow`), and the
 * Device-tools popover content (`deviceTools`). A running flow takes the
 * hero's place; the tabs stay underneath it.
 */
const DeviceWorkbench = ({
  lastStatusAt,
  // record
  wearable,
  recordStatus, // identifying | ready | notfound | error
  errorMsg,
  refreshing,
  historyKey,
  handleUpdated,
  onRefresh,
  // live
  connected,
  devCfg,
  liveFeed, // useLiveFeed result from the modal (feed rows, pulse, session)
  flowActive, // a flow (capture/calibrate/factory) owns the console
  currentPort,
  waking,
  commandsLocked,
  anyBusy,
  deviceRegistered,
  // header
  onClose,
  onDisconnect,
  // actions
  onExport,
  exportBusy,
  linesReceived,
  exportTotal,
  onFactoryTest,
  onCentrifuge,
  onCalibrate,
  onStartSession,
  onGoIdle,
  sessionBusy,
  onSyncTime,
  onUpdateFirmware,
  onEraseAll,
  storeEmpty = false,
  // slots
  centerFlow,
  // Most flows are forms and read better in a narrow measure. Export review
  // is not a form: it is a video beside a list of every impact on the device,
  // and 896px of an ultrawide monitor is the wrong place to judge footage.
  centerFlowWide,
  deviceTools,
}) => {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState("activity");
  const [sideTab, setSideTab] = useState("overview");
  const [toolsOpen, setToolsOpen] = useState(false);
  // Session opened from the Sessions tab; shows every unit assigned in that
  // session.
  const [openSessionId, setOpenSessionId] = useState(null);

  // A flow takes the hero's place at the top of the page. It is often
  // launched from further down (the Manufacturing tab), so bring it into view.
  const mainRef = useRef(null);
  const flowMounted = !!centerFlow;
  useEffect(() => {
    if (flowMounted) mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [flowMounted]);

  const ready = recordStatus === "ready" && wearable;
  const portShort = (currentPort || "").split("/").pop();
  const identity = wearableIdentity(wearable);
  const mac = devCfg?.mac || wearable?.ble_mac || null;
  const macText = mac ? colonMac(mac) : null;

  // Session history rides on superadmin-only endpoints; the workbench itself
  // is gated by Electron, not by role, so hide the tab from non-admins.
  const isSuperUser =
    JSON.parse(localStorage.getItem("user") || "null")?.is_superadmin === true;

  // Session count for the Sessions tab badge.
  const [sessionCount, setSessionCount] = useState(null);
  useEffect(() => {
    if (!isSuperUser || !macText) {
      setSessionCount(null);
      return undefined;
    }
    let active = true;
    betaAPI
      .getWearableAssignments({ macAddress: macText, page: 1, pageSize: 1 })
      .then((res) => {
        if (active) setSessionCount(res.data?.assignments?.total_count ?? null);
      })
      .catch((err) => {
        console.error("Failed to load wearable sessions", err);
        if (active) setSessionCount(null);
      });
    return () => {
      active = false;
    };
  }, [isSuperUser, macText, historyKey]);

  // ── Stage progress (validation tree) ──
  const validation = wearable?.validation;
  const stages = validation?.stages || [];
  const totalStages = stages.length;
  const completeStages = stages.filter((s) => s.status === "complete").length;
  // Centrifuge and drop share an order group, so more than one stage can be
  // active at once — the operator may do either.
  const activeStages = stages.filter((s) => s.status === "active");
  const activeStage = activeStages[0] || null;
  const multiActive = activeStages.length > 1;
  const allComplete = validation?.current_stage === "complete";
  const activeIdx = allComplete
    ? totalStages
    : activeStage
    ? stages.indexOf(activeStage) + 1
    : completeStages + 1;
  const stageLabel = (s) => s.label || getStageMeta(s.sub_stage).label;
  const activeLabel = multiActive
    ? activeStages.map(stageLabel).join(t("devices.workbench.stageJoiner"))
    : activeStage
    ? stageLabel(activeStage)
    : allComplete
    ? t("devices.workbench.complete")
    : t("devices.workbench.inValidation");

  let lastAttempt = null;
  for (const st of stages)
    for (const step of st.steps || []) {
      const ca = step.latest_attempt?.created_at;
      if (ca && (!lastAttempt || ca > lastAttempt)) lastAttempt = ca;
    }

  const conn = connState(
    {
      waking,
      commandsLocked,
      state: devCfg?.state,
      sub: devCfg?.sub,
    },
    t
  );
  const actionsDisabled = anyBusy || commandsLocked;

  // Start needs the v0.5.81 `session` command — hidden on older firmware
  // rather than shown broken.
  const showStartSession = fwAtLeast(devCfg?.fw, LIVE_STATE_FW_MIN);

  const flowBtn =
    "flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:border-[#DAD7D2] hover:bg-[#FBFAF9] disabled:opacity-50 " +
    FOCUS;
  const iconBtn = `flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151] disabled:pointer-events-none disabled:opacity-40 ${FOCUS}`;

  const tabs = [
    {
      id: "activity",
      label: t("devices.workbench.tabs.activity"),
      badge: connected ? (
        <TabCount tone="live">{t("common.status.live")}</TabCount>
      ) : null,
    },
    ...(isSuperUser
      ? [
          {
            id: "sessions",
            label: t("devices.workbench.tabs.sessions"),
            badge:
              sessionCount != null ? <TabCount>{sessionCount}</TabCount> : null,
          },
        ]
      : []),
    {
      id: "manufacturing",
      label: t("devices.workbench.tabs.manufacturing"),
      badge:
        ready && totalStages > 0 ? (
          allComplete ? (
            <TabCount tone="ok">✓</TabCount>
          ) : (
            <TabCount>{`${completeStages}/${totalStages}`}</TabCount>
          )
        ) : null,
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#FAFAF8]">
      {/* ── Top bar: identity on the left, link + tools on the right ── */}
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-[#EDEDEA] bg-white px-6 py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          {/* Back to the device chooser (and Update over Bluetooth): it
              disconnects, so not while a flow or an export is running. */}
          <button
            onClick={() => onDisconnect?.()}
            disabled={flowActive || exportBusy}
            title={
              flowActive || exportBusy
                ? t("devices.workbench.backUnavailable")
                : undefined
            }
            className={`-ml-2 mr-1 flex h-9 flex-shrink-0 items-center gap-1 rounded-lg px-2 text-[13px] font-medium text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#111827] disabled:cursor-not-allowed disabled:opacity-40 ${FOCUS}`}
          >
            <ChevronLeft size={16} />
            {t("devices.workbench.allDevices")}
          </button>
          <span className="h-5 w-px flex-shrink-0 bg-[#EDEDEA]" />
          {recordStatus === "identifying" ? (
            <>
              <span className="shimmer h-6 w-14 rounded" />
              <span className="shimmer h-3.5 w-28 rounded" />
            </>
          ) : ready ? (
            <>
              <span className="truncate font-mono text-[22px] font-bold leading-none tracking-tight text-[#111827]">
                {identity.primary}
              </span>
              {identity.secondary && (
                <span className="hidden truncate font-mono text-[12px] text-[#9CA3AF] sm:inline">
                  {identity.secondary}
                </span>
              )}
              <WearableStatusBadge status={wearable.status} size="sm" />
            </>
          ) : (
            <span className="flex items-center gap-2 truncate font-mono text-[15px] font-semibold text-[#111827]">
              {macText || portShort || t("devices.summaryCard.wearable")}
              {recordStatus === "notfound" && (
                <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 font-sans text-[11px] font-medium text-[#6B7280]">
                  {t("devices.summaryCard.notRegistered")}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {/* The link: how we're connected and how fresh the readings are.
              A sick link swaps in the amber state chip — that must be
              visible whatever tab you are on. */}
          {waking || commandsLocked ? (
            <span
              title={conn.title}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium"
              style={{ backgroundColor: conn.bg, color: conn.color }}
            >
              <Loader2 size={12} className="animate-spin" />
              {conn.label}
            </span>
          ) : (
            <span className="hidden items-center gap-1.5 text-[13px] text-[#6B7280] md:inline-flex">
              {connected ? (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                  {t("devices.workbench.connectedUsb")}
                  <span className="text-[#D1D5DB]">·</span>
                  <Freshness at={lastStatusAt} showDot={false} />
                </>
              ) : (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C4C7CD]" />
                  {t("devices.workbench.notConnected")}
                </>
              )}
            </span>
          )}

          <div className="relative ml-2">
            <button
              onClick={() => setToolsOpen((v) => !v)}
              aria-expanded={toolsOpen}
              className={`flex h-9 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] ${FOCUS}`}
            >
              {t("devices.workbench.deviceTools")}
              <ChevronDown
                size={14}
                className={`text-[#9CA3AF] transition-transform duration-150 ${
                  toolsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <DeviceToolsPopover
              open={toolsOpen}
              onClose={() => setToolsOpen(false)}
              className="right-0"
            >
              {deviceTools}
              <button
                onClick={() => {
                  setToolsOpen(false);
                  onDisconnect?.();
                }}
                className={`mt-2 flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:border-[#FECACA] hover:bg-[#FEF2F2] hover:text-[#DC2626] ${FOCUS}`}
              >
                <Unplug size={12} />
                {t("devices.workbench.disconnectDevice")}
              </button>
            </DeviceToolsPopover>
          </div>
          <button
            onClick={onRefresh}
            disabled={flowActive}
            title={
              flowActive
                ? t("devices.workbench.refreshUnavailable")
                : t("devices.workbench.refreshDevice")
            }
            aria-label={t("devices.workbench.refreshDevice")}
            className={iconBtn}
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
          <button
            onClick={onClose}
            title={t("common.actions.close")}
            aria-label={t("common.actions.close")}
            className={`flex h-9 w-9 items-center justify-center rounded-lg text-[#9CA3AF] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#374151] ${FOCUS}`}
          >
            <X size={17} />
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto px-8 py-7">
        {/* Hero, or the running flow in its place */}
        {centerFlow ? (
          <div className={centerFlowWide ? "" : "mx-auto max-w-4xl"}>
            {centerFlow}
          </div>
        ) : (
          <div className="mx-auto max-w-4xl">
            <LiveDeviceView
              connected={connected}
              devCfg={devCfg}
              conn={conn}
              waking={waking}
              commandsLocked={commandsLocked}
              anyBusy={anyBusy}
              flowActive={flowActive}
              showStartSession={showStartSession}
              sessionBusy={sessionBusy}
              onStartSession={onStartSession}
              onGoIdle={onGoIdle}
              onExport={onExport}
              exportBusy={exportBusy}
              linesReceived={linesReceived}
              exportTotal={exportTotal}
              onEraseAll={onEraseAll}
              storeEmpty={storeEmpty}
              onUpdateFirmware={onUpdateFirmware}
              onSyncTime={onSyncTime}
              pulse={liveFeed?.pulse || []}
              sessionStart={liveFeed?.sessionStart || null}
              sessionSummary={liveFeed?.sessionSummary || null}
              armingInfo={liveFeed?.armingInfo || null}
              eraseProgress={liveFeed?.eraseProgress || null}
              onDismissSummary={liveFeed?.dismissSessionSummary}
            />
          </div>
        )}

        <div className="mx-auto mt-8 max-w-4xl">
          {/* ── Tabs ── */}
          <div className="mb-4 flex items-center gap-7 border-b border-[#EDEDEA]">
            {tabs.map((tabDef) => {
              const on = mainTab === tabDef.id;
              return (
                <button
                  key={tabDef.id}
                  onClick={() => setMainTab(tabDef.id)}
                  className={`-mb-px flex items-center gap-2 border-b-2 pb-3 text-[15px] transition-colors duration-150 ${FOCUS} ${
                    on
                      ? "border-[#E07A3A] font-semibold text-[#111827]"
                      : "border-transparent font-medium text-[#6B7280] hover:text-[#374151]"
                  }`}
                >
                  {tabDef.label}
                  {tabDef.badge}
                </button>
              );
            })}
          </div>

          {mainTab === "activity" && (
            <LiveActivityFeed
              feed={liveFeed?.feed || []}
              connected={connected}
              hideTitle
            />
          )}

          {/* Sessions tab: where has this unit been used? Works off the MAC
              alone, so it answers even for devices with no manufacturing
              record. */}
          {mainTab === "sessions" &&
            isSuperUser &&
            (mac ? (
              <SessionHistory bleMac={mac} onSelectSession={setOpenSessionId} />
            ) : (
              <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-[#6B7280]">
                <Loader2 size={15} className="animate-spin text-[#E07A3A]" />
                {t("devices.workbench.waitingForIdentify")}
              </div>
            ))}

          {mainTab === "manufacturing" &&
            (recordStatus === "identifying" ? (
              // Skeleton, not a bare spinner — hints at the shape that's loading.
              <div>
                <div className="mb-3 flex items-center gap-2 text-[13px] text-[#6B7280]">
                  <Loader2 size={15} className="animate-spin text-[#E07A3A]" />
                  {commandsLocked
                    ? t("devices.workbench.waitingForConsole")
                    : t("devices.workbench.identifyingDevice")}
                </div>
                <div className="animate-pulse space-y-3">
                  <div className="h-6 w-40 rounded bg-[#EFEDE9]" />
                  <div className="h-1.5 w-full rounded-full bg-[#EFEDE9]" />
                  <div className="h-4 w-72 rounded bg-[#EFEDE9]" />
                  <div className="mt-6 h-40 w-full rounded-xl bg-[#F2F0EC]" />
                </div>
              </div>
            ) : recordStatus === "error" ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <AlertCircle size={26} className="text-[#DC2626]" />
                <p className="max-w-sm text-sm text-[#6B7280]">{errorMsg}</p>
                <button
                  onClick={onRefresh}
                  className={`flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] ${FOCUS}`}
                >
                  <RefreshCw size={14} />
                  {t("common.actions.retry")}
                </button>
              </div>
            ) : ready ? (
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
                <div className="min-w-0">
                  {/* Validation header — the active stage leads */}
                  <div className="mb-5">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF]">
                        {t("devices.workbench.validation")}
                      </span>
                      {totalStages > 0 && (
                        <span className="text-xs font-medium tabular-nums text-[#9CA3AF]">
                          {t("devices.workbench.stagesComplete", {
                            done: allComplete ? totalStages : completeStages,
                            total: totalStages,
                          })}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-baseline gap-2.5">
                      <h3
                        className="text-[20px] font-bold tracking-tight"
                        style={{ color: allComplete ? "#15803D" : "#111827" }}
                      >
                        {allComplete
                          ? t("devices.workbench.allStagesComplete")
                          : activeLabel}
                      </h3>
                      {allComplete ? (
                        <CheckCircle2 size={18} className="text-[#16A34A]" />
                      ) : multiActive ? (
                        <span className="text-[13px] font-medium text-[#9CA3AF]">
                          {t("devices.workbench.eitherOrder")}
                        </span>
                      ) : totalStages > 0 ? (
                        <span className="text-[13px] font-medium tabular-nums text-[#9CA3AF]">
                          {t("devices.workbench.stageOf", {
                            current: activeIdx,
                            total: totalStages,
                          })}
                        </span>
                      ) : null}
                    </div>
                    {totalStages > 0 && (
                      <div className="mt-3">
                        <StageRail
                          stages={stages}
                          allComplete={allComplete}
                          completeStages={completeStages}
                          totalStages={totalStages}
                        />
                      </div>
                    )}
                    <p className="mt-3 truncate font-mono text-xs tabular-nums text-[#9CA3AF]">
                      {formatMac(wearable.ble_mac)}
                      {wearable.batch_code
                        ? ` · ${t("devices.workbench.batch", {
                            code: wearable.batch_code,
                          })}`
                        : ""}
                      {wearable.unit_type ? ` · ${wearable.unit_type}` : ""}
                    </p>
                  </div>

                  {/* The validation flows — they only live here */}
                  <div className="mb-6 flex flex-wrap items-center gap-2">
                    <button
                      onClick={onFactoryTest}
                      disabled={actionsDisabled}
                      className={flowBtn}
                    >
                      <ClipboardCheck size={14} className="text-[#E07A3A]" />
                      {deviceRegistered === false
                        ? t("devices.workbench.registerUnit")
                        : t("devices.workbench.factoryTest")}
                    </button>
                    <button
                      onClick={onCentrifuge}
                      disabled={actionsDisabled}
                      title={t("devices.liveView.captureRunTitle")}
                      className={flowBtn}
                    >
                      <Disc3 size={14} className="text-[#E07A3A]" />
                      {t("devices.workbench.captureRun")}
                    </button>
                    <button
                      onClick={onCalibrate}
                      disabled={actionsDisabled}
                      className={flowBtn}
                    >
                      <Compass size={14} className="text-[#E07A3A]" />
                      {t("devices.workbench.calibrate")}
                    </button>
                  </div>

                  <ValidationFlow
                    wearable={wearable}
                    onUpdated={handleUpdated}
                  />
                </div>

                {/* Record side panel: only what the hero doesn't show */}
                <aside className="h-fit rounded-xl border border-[#EDEDEA] bg-white px-4 py-4">
                  <div className="mb-4 flex items-center gap-5 border-b border-[#F3F4F6]">
                    {[
                      {
                        id: "overview",
                        label: t("devices.workbench.sideTabs.overview"),
                      },
                      {
                        id: "history",
                        label: t("devices.workbench.sideTabs.history"),
                      },
                    ].map((tabDef) => {
                      const on = sideTab === tabDef.id;
                      return (
                        <button
                          key={tabDef.id}
                          onClick={() => setSideTab(tabDef.id)}
                          className={`-mb-px border-b-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors duration-150 ${FOCUS} ${
                            on
                              ? "border-[#E07A3A] text-[#E07A3A]"
                              : "border-transparent text-[#9CA3AF] hover:text-[#6B7280]"
                          }`}
                        >
                          {tabDef.label}
                        </button>
                      );
                    })}
                  </div>

                  {sideTab === "overview" ? (
                    <div>
                      {/* Printed label — the number the operator writes on the case */}
                      <div className="mb-3 flex items-center justify-between gap-3 border-b border-[#F3F4F6] pb-3">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF]">
                          {t("devices.workbench.printedLabel")}
                        </span>
                        <FieldLabelControl
                          wearable={wearable}
                          onUpdated={handleUpdated}
                        />
                      </div>
                      {wearable.status === "in_validation" && (
                        <p className="mb-3 text-[13px] leading-relaxed text-[#6B7280]">
                          {t("devices.workbench.finishChecklist")}
                        </p>
                      )}
                      <dl className="border-b border-[#F3F4F6] pb-3">
                        <InfoRow label={t("devices.workbench.info.stage")}>
                          {getStageMeta(wearable.validation_stage).label}
                        </InfoRow>
                        <InfoRow
                          label={t("devices.workbench.info.lastAttempt")}
                        >
                          {lastAttempt ? formatDateTime(lastAttempt) : "—"}
                        </InfoRow>
                      </dl>

                      {devCfg?.calStored != null && (
                        <div className="mt-3">
                          {devCfg.calStored ? (
                            <span className="inline-flex items-center gap-1 rounded bg-[#F0FDF4] px-1.5 py-0.5 text-[10px] font-medium text-[#16A34A]">
                              <BadgeCheck size={11} />
                              {t("devices.workbench.calibratedOn", {
                                date: fmtCalDate(devCfg.calSavedEpochS),
                              })}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-medium text-[#6B7280]">
                              {t("devices.workbench.notCalibrated")}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mt-4">
                        <StatusControl
                          wearable={wearable}
                          onUpdated={handleUpdated}
                        />
                      </div>
                    </div>
                  ) : (
                    <AttemptHistory
                      key={historyKey}
                      bleMac={wearable.ble_mac}
                    />
                  )}
                </aside>
              </div>
            ) : recordStatus === "notfound" ? (
              <div className="rounded-xl border border-dashed border-[#E3DFD9] bg-white/60 p-10 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF3ED]">
                  <PlugZap size={24} className="text-[#E07A3A]" />
                </div>
                <p className="text-sm font-semibold text-[#111827]">
                  {t("devices.workbench.notRegisteredYet")}
                </p>
                <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-[#6B7280]">
                  {t("devices.workbench.notRegisteredBody")}
                </p>
                <button
                  onClick={onFactoryTest}
                  disabled={actionsDisabled}
                  className={`mt-5 inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition duration-150 disabled:opacity-50 ${FOCUS}`}
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  <ClipboardCheck size={15} />
                  {t("devices.workbench.runFactoryTest")}
                </button>
              </div>
            ) : null)}
        </div>
      </main>

      <SessionWearablesModal
        show={Boolean(openSessionId)}
        onClose={() => setOpenSessionId(null)}
        sessionId={openSessionId}
        currentBleMac={mac}
        linkToUnits={false}
        onNavigateAway={onClose}
      />
    </div>
  );
};

export default DeviceWorkbench;
